import {
  ACTION,
  RequestCase,
  ReceiveCase,
  LoadCaseFailure,
  ReceiveCasePermissions,
  ChildItemSelection,
  RequestCaseRiskMatrixDefinition,
  ReceiveCaseRiskMatrixDefinition,
  ChangeViewMode
} from './action-types';
import { CaseState, ViewMode } from './types-store';
import { Reducer } from 'redux';
import { SchemaBuilder } from 'app/infrastructure/ui/builder';
import { RenderMode } from 'app/types-shared';
import {
  caseDataToStore,
  caseMetadataToStore
} from 'app/infrastructure/store/normalize';
import { reducers as draftReducer } from './draft/reducers';
import { reducers as editReducer } from './edit/reducers';
import { reducers as createReducer } from './create/reducers';
import { reducers as caseConnectionReducer } from './features/connected-cases/reducers';
import { reducers as caseRiskEvaluationReducer } from './features/loss-potential-evaluation/reducers';
import { reducers as searchExternalProviderReducer } from './features/search-external-provider/reducers';
import { reducers as callsToActionsReducer } from './features/calls-to-actions/reducers';
import { ActionMap } from 'app/area/case';
import { createUiReducers } from 'app/infrastructure/ui/form/reducers';
import { getDraftId } from 'app/infrastructure/store/query';
import { clientCache } from 'app/infrastructure/universal';
import { viewModeCacheKey } from 'app/area/case/constants';

export const INITIAL_CASE_STATE: CaseState = {
  editing: null,
  stateForCaseIds: {},
  entities: {},
  renderMode: RenderMode.read,
  fetching: true,
  schema: null,
  riskMatrixDefinitions: {},
  texts: {} as any,
  tempdata: {},
  metadata: undefined,
  viewMode: clientCache.getItem(viewModeCacheKey) || ViewMode.tabs
};

/**
 * Reducers for case. Pattern to be able to extract modules when it grows.
 */
const reducers: ActionMap = {
  /**
   * Run when case starts requesting data
   */
  [ACTION.REQUEST_CASE]: (state: CaseState, action: RequestCase): CaseState => {
    return {
      ...state,
      ...{
        schema: undefined,
        selectedId: undefined,
        entities: {},
        uiDefinition: undefined,
        permissions: undefined,
        fetching: true,
        activeChildItem: undefined,
        metadata: undefined
      }
    };
  },

  /**
   * Run when case has finished requesting data
   */
  [ACTION.RECEIVE_CASE]: (state: CaseState, action: ReceiveCase): CaseState => {
    const docValues: any = caseDataToStore(action.data);
    const metadata = caseMetadataToStore(action.data);
    const pk = getDraftId(docValues);

    return {
      ...state,
      ...{
        selectedId: pk,
        entities: {
          [pk]: docValues
        },
        metadata: {
          [pk]: metadata
        },
        schema: SchemaBuilder.create(state.renderMode, action.uiDefinition),
        uiDefinition: action.uiDefinition,
        fetching: false
      }
    } as CaseState;
  },

  /**
   * Run when case has failed to download data
   */
  [ACTION.FAIL_CASE_DATA]: (
    state: CaseState,
    action: LoadCaseFailure
  ): CaseState => {
    return {
      ...state,
      ...{
        selectedId: null,
        fetching: false,
        schema: null
      }
    };
  },

  /**
   * Run when case has finished requesting access data
   */
  [ACTION.RECEIVE_CASE_PERMISSIONS]: (
    state: CaseState,
    action: ReceiveCasePermissions
  ): CaseState => {
    return {
      ...state,
      ...{
        permissions: action.data
      }
    };
  },

  /**
   * Clear the previous risk matrix definition and await the next requested
   */
  [ACTION.REQUEST_CASE_RISK_MATRIX_DEFINITION]: (
    state: CaseState,
    action: RequestCaseRiskMatrixDefinition
  ): CaseState => {
    return {
      ...state,
      ...{
        riskMatrixDefinitions: {
          ...state.riskMatrixDefinitions,
          ...{
            [action.caseTypeId]: {
              fetching: true
            }
          }
        }
      }
    };
  },

  /**
   * Once the risk matrix definition is available, add it to store.
   */
  [ACTION.RECEIVE_CASE_RISK_MATRIX_DEFINITION]: (
    state: CaseState,
    action: ReceiveCaseRiskMatrixDefinition
  ): CaseState => {
    return {
      ...state,
      ...{
        riskMatrixDefinitions: {
          ...state.riskMatrixDefinitions,
          ...{
            [action.caseTypeId]: {
              definition: {
                ...action.riskMatrixDefinition,
                ...{ applicationId: action.caseTypeId }
              },
              fetching: false
            }
          }
        }
      }
    };
  },

  [ACTION.SELECT_DOCUMENT_ITEM]: (
    state: CaseState,
    action: ChildItemSelection
  ): CaseState => {
    return {
      ...state,
      ...{
        activeChildItem: action.key
      }
    };
  },
  [ACTION.CHANGE_VIEW_MODE]: (
    state: CaseState,
    action: ChangeViewMode
  ): CaseState => {
    return {
      ...state,
      viewMode: action.mode
    };
  }
};

const uiEngineReducers = createUiReducers((action: any) => true);

/**
 * Export a function that maps the given action to our ActionMap
 */
export const reducer: Reducer<CaseState> = (
  state: CaseState = INITIAL_CASE_STATE,
  action: any
) => {
  const allCaseReducers = {
    ...reducers,

    ...draftReducer,

    ...editReducer,

    ...createReducer,

    ...caseConnectionReducer,

    ...caseRiskEvaluationReducer,

    ...searchExternalProviderReducer,

    ...callsToActionsReducer
  };

  const r: any = allCaseReducers[action.type] || uiEngineReducers;

  return (r && r(state, action)) || state;
};
